import React, { Component } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import AvatarEditor from 'react-avatar-editor'
import InputRange from 'react-input-range'
import ImmutablePropTypes from 'react-immutable-proptypes'

import Wrapper from './Wrapper'
import ModalWrapper from './ModalWrapper'
import ButtonDefault from 'app/components/ButtonDefault'
import RotateRightSVG from 'app/components/SVGS/RotateRight'
import Select from 'app/components/Select'
import CheckBox from 'app/components/CheckBox'

import './styles.scss'

const baseClass = 'image-cropper'

class ImageCropper extends Component {
  static defaultProps = {
    outputWidth: 400,
    outputHeight: 400,
    renderWidth: 240,
    renderHeight: 240,
    border: 0,
    isOpen: false,
    scale: {
      min: 50,
      max: 200,
    },
    rotate: false,
    isModal: false,
  }

  static propTypes = {
    outputWidth: PropTypes.number,
    outputHeight: PropTypes.number,
    renderWidth: PropTypes.number,
    renderHeight: PropTypes.number,
    border: PropTypes.number,
    isOpen: PropTypes.bool,
    rotate: PropTypes.bool,
    isModal: PropTypes.bool,
    scale: PropTypes.shape({
      min: PropTypes.number,
      max: PropTypes.number,
    }),
    handleWillUnmount: PropTypes.func,
    organizations: ImmutablePropTypes.list,
    handleOnChange: PropTypes.func,
  }

  constructor(...opts) {
    super(...opts)
    this.state = {
      scale: 100,
      rotate: 0,
      isConfirmRewriteImage: false,
    }
    this.timeout = null
  }

  componentWillUnmount() {
    this.timeout && clearTimeout(this.timeout)
    this.props.handleWillUnmount && this.props.handleWillUnmount()
  }

  componentDidMount() {
    this.timeout && clearTimeout(this.timeout)
  }

  renderInnerComponent = () => {
    const {
      t,
      file,
      organizations,
      handleOnChange,
      imageForReplace,
      isButtonDisable,
      isShowCancelButton = true,
    } = this.props
    const fileUrlOrName = file.indexOf && file.indexOf('https') === 0 ? `${file}?random=${new Date().getTime()}` : file
    return (
      <div className={baseClass}>
        <div
          className={classNames(`${baseClass}__body`, {
            [`${baseClass}__body--rotate`]: this.props.rotate,
          })}
        >
          {this.props.rotate && (
            <div
              className={classNames(`${baseClass}__arrow`, `${baseClass}__arrow--left`)}
              onClick={() => {
                this.setState({
                  rotate: (this.state.rotate - 90) % 360,
                })
              }}
            >
              <RotateRightSVG />
            </div>
          )}
          <div
            style={{
              width: this.props.renderWidth + 2,
              height: this.props.renderHeight + 2,
              minWidth: this.props.renderWidth + 2,
              minHeight: this.props.renderHeight + 2,
            }}
          >
            <AvatarEditor
              style={{
                verticalAlign: 'middle',
                transform: `scale(${this.props.renderWidth / this.props.outputWidth})`,
                transformOrigin: '0 0',
              }}
              // @see https://github.com/mosch/react-avatar-editor/issues/156 regarding param "random"
              image={fileUrlOrName}
              width={this.props.outputWidth}
              height={this.props.outputHeight}
              border={this.props.border}
              color={[255, 255, 255, 0.6]}
              scale={this.state.scale / 100}
              rotate={this.state.rotate}
              ref={editor => {
                this.editor = editor
              }}
              crossOrigin="anonymous"
            />
          </div>
          {this.props.rotate && (
            <div
              className={`${baseClass}__arrow`}
              onClick={() => {
                this.setState({
                  rotate: (this.state.rotate + 90) % 360,
                })
              }}
            >
              <RotateRightSVG />
            </div>
          )}
        </div>
        <div className={`${baseClass}__input-range-container`}>
          <InputRange
            formatLabel={value => `${value}%`}
            minValue={this.props.scale.min}
            maxValue={this.props.scale.max}
            value={this.state.scale}
            onChange={value => this.setState({ scale: value })}
          />
          {!!imageForReplace && (
            <CheckBox
              label={t(
                'admin:This image name is already used. If you continue the upload, the previous image with the same name will be replaced.'
              )}
              title={'Confirm rewrite image'}
              input={{
                value: this.state.isConfirmRewriteImage,
                onChange: () => {
                  this.setState({
                    isConfirmRewriteImage: !this.state.isConfirmRewriteImage,
                  })
                },
              }}
              className={`${baseClass}__checkbox`}
            />
          )}
        </div>
        <div className={`${baseClass}__footer`}>
          {organizations && (
            <Select
              isRowView={false}
              showLabel={false}
              name="activityScopeImage"
              options={organizations}
              getValue={option => `${option.get('id')}_${option.get('domain')}`}
              getKey={option => option.get('domain')}
              getLabel={option => option.get('domain')}
              input={{
                onChange: value => {
                  handleOnChange(value, file.name)
                  this.setState({ isConfirmRewriteImage: false })
                },
              }}
              className={`${baseClass}__select`}
            />
          )}
          <ButtonDefault
            xs
            style={{ padding: '24px 60px' }}
            primary={!imageForReplace || (!!imageForReplace && this.state.isConfirmRewriteImage)}
            disabled={(isButtonDisable && organizations) || (!!imageForReplace && !this.state.isConfirmRewriteImage)}
            onClick={() => {
              if (this.editor) {
                const canvas = this.editor.getImage()
                const canvasScaled = this.editor.getImageScaledToCanvas()
                this.props.onSave(canvas, canvasScaled, this.state.scale, this.state.isConfirmRewriteImage)
                this.timeout = setTimeout(() => {
                  this.setState({
                    scale: 100,
                  })
                }, 500)
                this.props.isModal && this.props.onRequestClose()
              }
            }}
          >
            {t('common:Ok')}
          </ButtonDefault>
          {isShowCancelButton && (
            <ButtonDefault
              style={{ padding: '24px 60px' }}
              xs
              primary
              onClick={() => {
                this.props.onCancel()
                this.props.isModal && this.props.onRequestClose()
                this.props.setStateToDefault()
              }}
            >
              {t('common:Cancel')}
            </ButtonDefault>
          )}
        </div>
      </div>
    )
  }

  render() {
    return this.props.isModal ? (
      <ModalWrapper>{this.renderInnerComponent()}</ModalWrapper>
    ) : (
      <Wrapper isOpen={this.props.isOpen}>{this.renderInnerComponent()}</Wrapper>
    )
  }
}

export default ImageCropper
